<template>
  <div
    :class="[
      'tw:flex tw:flex-col tw:font-serif tw:gap-2 tw:p-2 tw:border tw:transition-colors tw:duration-200 tw:rounded-lg tw:w-44 tw:min-h-80 shadow',
      {
        'tw:bg-[#404040]': isHired,
        'tw:border-[#49DDAA]': selected,
        'tw:border-blue-8': !selected,
        selected: !isEliminated && selected,
        backlight: !isEliminated && !selected && !isHired,
        'tw:bg-[#081B25]/50 tw:border-transparent': isEliminated,
        'tw:cursor-pointer': selectionMode && !isEliminated && !isHired
      }
    ]"
    @click="handleSelect"
  >
    <div class="tw:z-[2] tw:relative tw:flex tw:flex-col tw:items-center tw:gap-1">
      <h6
        :class="[
          'tw:font-bold tw:text-white tw:top-0 tw:absolute tw:text-center tw:text-xl',
          {
            'tw:opacity-50': isEliminated
          }
        ]"
      >
        {{ $t(name) }}
      </h6>
      <div class="tw:pt-16 tw:h-72">
        <img v-if="isEliminated" src="/img/halloween/eliminated.png" />
        <img
          v-else
          :src="`/img/halloween/characters/${character?.characterType}.png`"
          class="tw:h-full tw:object-contain"
        />
      </div>
    </div>
    <ul
      :class="[
        'tw:space-y-2 tw:z-[2]',
        {
          'tw:opacity-50': isEliminated
        }
      ]"
    >
      <li v-for="[stat, value] in Object.entries(stats)" :key="stat" class="tw:flex tw:flex-col">
        <div class="tw:flex tw:justify-between tw:items-center tw:text-white tw:leading-none">
          <span>{{ $t(statsLocaleMap[stat as unknown as CHARACTER_STAT]) }}</span>
          <span>{{ value }}</span>
        </div>
        <div class="tw:flex tw:gap-px tw:border tw:border-[#3B7285] tw:rounded-lg tw:overflow-hidden">
          <div
            :class="[
              'tw:flex-1 tw:h-1',
              {
                healthbar: !isEliminated
              }
            ]"
          />
        </div>
      </li>
    </ul>
    <p v-if="isHired" class="tw:py-2 tw:font-bold tw:text-white tw:text-center tw:leading-5">
      {{ $t('halloweenCharacterHireModalHireHired') }}
    </p>
    <div v-else-if="selectionMode && character" class="tw:z-[2] tw:flex tw:flex-col tw:items-center">
      <p class="tw:mx-auto tw:leading-5">{{ $t('halloweenCharacterHireModalHireForText') }}</p>
      <ul class="tw:flex tw:flex-col tw:items-center">
        <li v-for="item in characterTokens" :key="item.id" class="tw:flex tw:items-center tw:gap-1 tw:text-white">
          <img class="tw:w-6 tw:h-6" :src="halloweenItemRegistry.resolveIcon(item)" />
          <span style="font-size: 16px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis">{{
            $t(item.name, 0)
          }}</span>
          <span class="tw:place-self-end">{{ item.quantity }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ParsedCharacterData } from '~/types/halloween/expedition';
import { HalloweenItem } from '~/types/halloween/items';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';
import { CHARACTER_STAT, CHARACTER_STATUS } from '../model/consts';
import type { TCharacterStats } from '../model/types';
import { statsLocaleMap } from '../lib/stats-locale-map';

const {
  selectionMode = true,
  status = CHARACTER_STATUS.AVAILABLE,
  character
} = defineProps<{
  name: string;
  stats: TCharacterStats;
  selectionMode?: boolean;
  status: CHARACTER_STATUS;
  character: ParsedCharacterData;
}>();

const characterTokens = computed(() => [
  halloweenItemRegistry.withQuantity(HalloweenItem.GoldOre, character.goldOrePrice),
  halloweenItemRegistry.withQuantity(HalloweenItem.Pearl, character.pearlPrice)
]);

const selected = defineModel<ParsedCharacterData | undefined>({ default: undefined });

const isEliminated = computed(() => status === CHARACTER_STATUS.ELIMINATED);
const isHired = computed(() => status === CHARACTER_STATUS.HIRED);

function handleSelect() {
  if (!selectionMode || !character || isHired.value || isEliminated.value) return;
  selected.value ? (selected.value = undefined) : (selected.value = character);
}
</script>

<style scoped lang="scss">
// еще раз мне придется этим заниматься >:(

// Миксины для переиспользования
@mixin card-base {
  position: relative;
  background: radial-gradient(60% 70% at 80% 80%, #040f14 0, #081b25 100%);
  overflow: hidden;
}

@mixin pseudo-layer($background, $opacity: 1) {
  content: '';
  position: absolute;
  inset: 0;
  background: $background;
  opacity: $opacity;
  transition: opacity 0.3s ease;
  z-index: 1;
}

// Базовые стили карточки
.backlight {
  @include card-base;
  transition: background 0.3s ease;

  &::before {
    @include pseudo-layer(radial-gradient(ellipse 100% 100% at 50% 0%, rgba(0, 131, 173, 0.7) 0, transparent 70%));
  }

  &:hover::before {
    opacity: 0;
  }

  &::after {
    @include pseudo-layer(radial-gradient(100% 100% at 50% 0, #0083ad 0, rgba(0, 0, 0, 0) 100%), 0);
  }

  &:hover::after {
    opacity: 1;
  }
}

// Выбранное состояние
.selected {
  @include card-base;

  &::before {
    @include pseudo-layer(linear-gradient(0deg, #1d471d, #1d471d));
  }

  &::after {
    @include pseudo-layer(radial-gradient(100% 100% at 50% -20%, #60ff7c 0, rgba(0, 0, 0, 0) 100%));
  }
}

.shadow {
  box-shadow: 1.5px 3px 4px 0px rgba(0, 0, 0, 0.25);
}

.healthbar {
  background: linear-gradient(90deg, #15cde3 0%, #2ddec2 100%);
}
</style>
